import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { listStoneRequirements, listStoneQuotes } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import StoneTabs from '@/components/stones/StoneTabs';
import StonesHeader from '@/components/stones/StonesHeader';
import HeroThumb from '@/components/stones/HeroThumb';
import { STONE_QUOTE_STATUSES } from '@/utils/constants';
import { Bell, Gem, MessageCircle, Send, Inbox, Check, AlertTriangle } from 'lucide-react';

const LAST_SEEN_KEY = (userId) => `stones_notifications_last_seen_${userId || 'anon'}`;

// Activity feed for the stones module.
// Admin sees: new submitted quotes (awaiting decision), revised quotes from suppliers.
// Supplier sees: new requirements they were invited to, revise requests on their quotes.
// Items are derived from existing data (no separate notifications collection yet).
// "Unread" = created after the timestamp the user last opened this page (localStorage).
export default function StoneNotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isSupplier = user?.role === 'stone_supplier';

  const [reqs, setReqs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSeen, setLastSeen] = useState(() => {
    try { return parseInt(localStorage.getItem(LAST_SEEN_KEY(user?.id)) || '0', 10); } catch { return 0; }
  });

  useEffect(() => {
    Promise.all([listStoneRequirements(), listStoneQuotes()])
      .then(([r, q]) => { setReqs(r.data.data || []); setQuotes(q.data.data || []); })
      .catch(() => toast.error('Failed to load notifications'))
      .finally(() => setLoading(false));
  }, []);

  // Build the activity feed (newest first)
  const feed = useMemo(() => {
    const items = [];
    if (isAdmin) {
      // Each submitted quote is a notification (admin needs to decide)
      for (const q of quotes) {
        const ts = new Date(q.updated_at || q.created_at).getTime();
        if (q.status === 'submitted') {
          items.push({
            id: `quote-sub-${q.id}-${ts}`,
            ts,
            type: q.revision_count > 0 ? 'quote_revised' : 'quote_submitted',
            icon: q.revision_count > 0 ? Send : Inbox,
            color: q.revision_count > 0 ? 'violet' : 'amber',
            title: q.revision_count > 0
              ? `${q.supplier_name} revised their quote on ${q.requirement_no || ''}`
              : `${q.supplier_name} submitted a quote on ${q.requirement_no || ''}`,
            body: q.price_total != null
              ? `${q.currency} ${Number(q.price_total).toLocaleString()}${q.price_per_ct != null ? ` · ${q.currency} ${q.price_per_ct}/ct` : ''}`
              : null,
            href: `/stones?req=${q.requirement_id}&quote=${q.id}`,
            req: reqs.find(r => r.id === q.requirement_id),
          });
        }
        if (q.status === 'accepted' && q.decided_at) {
          items.push({
            id: `quote-acc-${q.id}`,
            ts: new Date(q.decided_at).getTime(),
            type: 'quote_accepted',
            icon: Check,
            color: 'emerald',
            title: `You accepted ${q.supplier_name}'s quote on ${q.requirement_no || ''}`,
            body: null,
            href: `/stones?req=${q.requirement_id}&quote=${q.id}`,
            req: reqs.find(r => r.id === q.requirement_id),
          });
        }
      }
    }
    if (isSupplier) {
      // New requirements for supplier
      for (const r of reqs) {
        if (r.status === 'open') {
          const myQuote = quotes.find(q => q.requirement_id === r.id);
          if (!myQuote) {
            items.push({
              id: `req-new-${r.id}`,
              ts: new Date(r.created_at).getTime(),
              type: 'requirement_new',
              icon: Gem,
              color: 'teal',
              title: `New requirement: ${r.gemstone_type}`,
              body: `${r.requirement_no}${r.size_mm ? ` · ${r.size_mm}` : ''}${r.qty ? ` · Qty ${r.qty}` : ''}`,
              href: `/stones?req=${r.id}`,
              req: r,
            });
          }
        }
      }
      // Revise requests on supplier's quotes
      for (const q of quotes) {
        if (q.status === 'revise_requested' && q.revise_request?.requested_at) {
          items.push({
            id: `quote-revreq-${q.id}`,
            ts: new Date(q.revise_request.requested_at).getTime(),
            type: 'revise_requested',
            icon: AlertTriangle,
            color: 'violet',
            title: `Admin asked for a revised quote on ${q.requirement_no || ''}`,
            body: q.revise_request.notes || (
              q.revise_request.target_price_total != null
                ? `Target: ${q.currency} ${Number(q.revise_request.target_price_total).toLocaleString()}`
                : null
            ),
            href: `/stones/${q.requirement_id}/quote`,
            req: reqs.find(r => r.id === q.requirement_id),
          });
        }
      }
    }
    return items.sort((a, b) => b.ts - a.ts);
  }, [reqs, quotes, isAdmin, isSupplier]);

  const unreadCount = feed.filter(i => i.ts > lastSeen).length;

  // Mark all as read when the user lingers on the page for >1s
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => {
      const now = Date.now();
      try { localStorage.setItem(LAST_SEEN_KEY(user?.id), String(now)); } catch {}
      setLastSeen(now);
    }, 1000);
    return () => clearTimeout(t);
  }, [loading, user?.id]);

  const colorClass = (c, isUnread) => {
    const base = {
      amber: 'bg-amber-50 border-amber-200 text-amber-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
    }[c] || 'bg-slate-50 border-slate-200 text-slate-700';
    return isUnread ? base : 'bg-white border-slate-200 text-slate-700';
  };

  return (
    <CollapsibleLayout>
      <StoneTabs active="notifications" />
      <StonesHeader
        icon={Bell}
        title="Notifications"
        subtitle={isAdmin ? 'Quotes submitted, revisions, decisions.' : 'New requirements and revision requests.'}
      >
        {unreadCount > 0 && (
          <span className="text-[11px] bg-teal-600 text-white px-2 py-0.5 rounded-full font-semibold">{unreadCount} new</span>
        )}
      </StonesHeader>
      <div className="max-w-[800px] mx-auto p-4 sm:p-6 space-y-4">

        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-12">Loading…</p>
        ) : feed.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <Bell size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-muted-foreground text-sm">You're all caught up.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {feed.map(item => {
              const isUnread = item.ts > lastSeen;
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button onClick={() => navigate(item.href)}
                    className={`w-full text-left rounded-xl border p-3 sm:p-4 flex gap-3 hover:shadow-sm transition-all ${colorClass(item.color, isUnread)} ${isUnread ? 'shadow-sm' : ''}`}
                    data-testid={`notif-${item.id}`}>
                    {item.req?.sample_images?.[0] ? (
                      <HeroThumb image={item.req.sample_images[0]} size={48} />
                    ) : (
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-white/60 shrink-0">
                        <Icon size={20} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium text-sm leading-snug">{item.title}</div>
                        {isUnread && <span className="w-2 h-2 rounded-full bg-teal-500 shrink-0 mt-1.5" title="New" />}
                      </div>
                      {item.body && <div className="text-xs text-slate-600 mt-1">{item.body}</div>}
                      <div className="text-[10px] text-slate-500 mt-1.5">{relativeTime(item.ts)}</div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </CollapsibleLayout>
  );
}

function relativeTime(ts) {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}
